import { ActivatedRoute, Router } from '@angular/router';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { SupportHubStore } from '../support-hub.store';

interface TicketItem {
  id: number;
  key: string;
  title: string;
  type: string;
  status: string;
}

interface EvidenceItem {
  title: string;
  subtitle: string;
  kind: string;
}

interface VersionItem {
  id: number;
  label: string;
  date: string;
  status: string;
  summary: string;
  changeHistoryHtml: string;
  author: string;
  color: string;
  tickets: TicketItem[];
  changes: string[];
  evidence: EvidenceItem[];
}

@Component({
  selector: 'app-support-hub-versions',
  standalone: true,
  templateUrl: './support-hub-versions.component.html',
  styleUrl: './support-hub-versions.component.scss'
})
export class SupportHubVersionsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private summaryRequestVersionId: number | null = null;
  private selectedVersionIdFromQuery: number | null = null;
  private readonly emptyVersion: VersionItem = {
    id: 0,
    label: '',
    date: '',
    status: '',
    summary: '',
    changeHistoryHtml: '',
    author: '',
    color: '',
    tickets: [],
    changes: [''],
    evidence: [{
      title: 'Evidence placeholder',
      subtitle: 'Evidence details will be added later.',
      kind: 'Placeholder'
    }]
  };

  activeTab: 'changes' | 'tickets' | 'evidence' | 'version' = 'changes';
  loadingVersions = false;
  loadError = '';
  private lastLoadedUseCaseId: number | null = null;

  versions: VersionItem[] = [];

  selectedVersion: VersionItem = this.emptyVersion;

  constructor(
    private readonly apiService: MfgappsApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly store: SupportHubStore
  ) {}

  async ngOnInit(): Promise<void> {
    await this.store.loadLookup();

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const value = Number(params.get('versionId'));
        this.selectedVersionIdFromQuery = Number.isFinite(value) && value > 0 ? value : null;
      });

    this.store.selectedUseCaseIdSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (useCaseId) => {
        const nextUseCaseId = useCaseId ?? this.store.getDefaultUseCaseId();
        if (!nextUseCaseId || nextUseCaseId === this.lastLoadedUseCaseId) {
          return;
        }

        this.lastLoadedUseCaseId = nextUseCaseId;
        await this.loadVersions(nextUseCaseId);
      });

    const initialUseCaseId = this.store.selectedUseCaseIdValue ?? this.store.getDefaultUseCaseId();
    if (initialUseCaseId && initialUseCaseId !== this.lastLoadedUseCaseId) {
      this.lastLoadedUseCaseId = initialUseCaseId;
      await this.loadVersions(initialUseCaseId);
    }
  }

  selectVersion(id: number): void {
    const found = this.versions.find((version) => version.id === id);
    if (!found) {
      return;
    }

    this.selectedVersion = found;
    this.activeTab = 'changes';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.store.getUseCaseQueryParams({ versionId: found.id }),
      replaceUrl: true
    });
    void this.loadVersionSummary(found.id);
  }

  openTicket(ticket: TicketItem): void {
    this.router.navigate(['/support-hub/request/detail', ticket.id]);
  }

  initials(name: string): string {
    const initials = name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase();

    return initials || 'NA';
  }

  private async loadVersions(useCaseId: number): Promise<void> {
    this.loadingVersions = true;
    this.loadError = '';

    try {
      const response = await this.apiService.getFeedbackVersions(useCaseId, 'feedback');
      const versionList = Array.isArray(response?.result) ? response.result : [];
      const completedStatusIds = this.store.getCompletedVersionStatusIds();
      this.versions = versionList
        .filter((version: any) => !completedStatusIds.has(version?.version_status_id))
        .map((version: any) => this.toVersionItem(version));

      if (this.versions.length === 0) {
        this.selectedVersion = this.emptyVersion;
        return;
      }

      const selectionFromQuery = this.selectedVersionIdFromQuery
        ? this.versions.find(item => item.id === this.selectedVersionIdFromQuery)
        : undefined;
      const currentSelection = this.versions.find(item => item.id === this.selectedVersion.id);
      this.selectedVersion = selectionFromQuery ?? currentSelection ?? this.versions[0];
      await this.loadVersionSummary(this.selectedVersion.id);
    } catch (error: any) {
      this.versions = [];
      this.selectedVersion = this.emptyVersion;
      this.loadError = error?.message ?? 'Unable to load versions. Please try again later.';
    } finally {
      this.loadingVersions = false;
    }
  }

  private toVersionItem(version: any): VersionItem {
    return {
      id: Number(version?.version_id) || 0,
      label: String(version?.version_name ?? 'TBD'),
      date: this.formatVersionDate(version?.created_date),
      status: String(version?.version_status ?? 'TBD'),
      summary: String(version?.version_description ?? 'Version details will be available soon.'),
      changeHistoryHtml: '',
      author: String(version?.owner ?? version?.created_by ?? 'TBD'),
      color: this.resolveVersionColor(version),
      tickets: this.toTicketItems(version?.tickets),
      changes: ['Changes will be added later.'],
      evidence: [{
        title: 'Evidence placeholder',
        subtitle: 'Evidence details will be added later.',
        kind: 'Placeholder'
      }]
    };
  }

  private formatVersionDate(createdDate: unknown): string {
    if (typeof createdDate !== 'string' || !createdDate.trim()) {
      return '';
    }

    const raw = createdDate.trim();
    const isoLike = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const normalized = isoLike.replace(/(\.\d{3})\d+$/, '$1');
    let parsed = new Date(normalized);

    if (Number.isNaN(parsed.getTime())) {
      const dateOnly = raw.split(' ')[0];
      parsed = new Date(`${dateOnly}T00:00:00`);
    }

    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  private toTicketItems(tickets: any): TicketItem[] {
    if (!Array.isArray(tickets)) {
      return [];
    }

    return tickets.map((ticket: any) => ({
      id: Number(ticket?.feedback_id) || 0,
      key: `#${ticket?.feedback_id ?? 'TBD'}`,
      title: String(ticket?.title ?? 'Untitled ticket'),
      type: this.store.getWorkCategoryNameById(ticket?.workcategory_id) || 'TBD',
      status: this.store.getStatusNameById(ticket?.status_id ?? null, 'TBD')
    }));
  }

  private resolveVersionColor(version: any): string {
    if (version?.color_code) {
      return String(version.color_code);
    }

    const mappedStatus = this.store.versionStatusOptions.find(item => item.statusId === version?.version_status_id);
    return mappedStatus?.colorCode || '#94a3b8';
  }

  private async loadVersionSummary(versionId: number): Promise<void> {
    if (!versionId) {
      return;
    }

    this.summaryRequestVersionId = versionId;

    try {
      const response = await this.apiService.getVersionSummary(versionId);
      if (this.summaryRequestVersionId !== versionId) {
        return;
      }

      const summaryHtml = this.extractVersionSummaryHtml(response);
      const targetVersion = this.versions.find(version => version.id === versionId);
      if (!targetVersion) {
        return;
      }

      targetVersion.changeHistoryHtml = summaryHtml;
      if (this.selectedVersion.id === versionId) {
        this.selectedVersion = { ...targetVersion };
      }
    } catch {
      const targetVersion = this.versions.find(version => version.id === versionId);
      if (!targetVersion) {
        return;
      }

      targetVersion.changeHistoryHtml = '';
      if (this.selectedVersion.id === versionId) {
        this.selectedVersion = { ...targetVersion };
      }
    }
  }

  private extractVersionSummaryHtml(response: any): string {
    const result = response?.result;

    if (typeof result === 'string' && result.trim()) {
      return result;
    }

    if (Array.isArray(result?.summary)) {
      const summaryItems = result.summary
        .map((entry: any) => typeof entry?.summary_text === 'string' ? entry.summary_text.trim() : '')
        .filter((text: string) => !!text);

      if (summaryItems.length > 0) {
        return summaryItems.join('<br/><br/>');
      }
    }

    if (typeof result?.summary === 'string' && result.summary.trim()) {
      return result.summary;
    }

    if (typeof result?.summary_text === 'string' && result.summary_text.trim()) {
      return result.summary_text;
    }

    if (typeof response?.summary === 'string' && response.summary.trim()) {
      return response.summary;
    }

    return '';
  }
}